/**
 * Tax invoice rule for GST credits (PRD R3.4).
 *
 * Source: ATO, "Tax invoices"
 * (https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices).
 * A business may only claim a GST credit for a purchase priced above A$82.50 (GST inclusive)
 * if it holds a tax invoice, which must show the supplier's ABN. The threshold applies in
 * financial year 2026-27 (unchanged since 2000). The ATO page returned 403 to the automated
 * check on 2026-10-21, so re-confirm it at the next annual rate review.
 */

/** A$82.50 GST inclusive, in cents. A tax invoice is needed above this amount. */
export const TAX_INVOICE_THRESHOLD_MINOR = 8250

export interface GstCreditInput {
  totalMinor: number
  gstMinor: number
  /** 0..100. */
  businessPct: number
  hasAbn: boolean
  isTaxInvoice: boolean
}

export interface GstCreditEstimate {
  creditMinor: number
  /** Set when a credit is lost because the purchase lacks the tax invoice evidence. */
  warning: "missing_tax_invoice" | null
}

/** True when the purchase is above the threshold but lacks an ABN or tax invoice. */
export function lacksTaxInvoiceEvidence(input: {
  totalMinor: number
  hasAbn: boolean
  isTaxInvoice: boolean
}): boolean {
  return input.totalMinor > TAX_INVOICE_THRESHOLD_MINOR && (!input.hasAbn || !input.isTaxInvoice)
}

/** Estimated claimable GST: gst * business% rounded half up; zero when evidence is missing. */
export function gstCreditEstimate(input: GstCreditInput): GstCreditEstimate {
  if (lacksTaxInvoiceEvidence(input)) return { creditMinor: 0, warning: "missing_tax_invoice" }
  const creditMinor = Math.floor((input.gstMinor * input.businessPct + 50) / 100)
  return { creditMinor, warning: null }
}
